import { useId, useState } from "react";
import { type CarrierTask, useCarrierMe } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";
import { taskLabel } from "@/lib/task-reference";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";

export function TaskReceiptAction({
  task,
  pending,
  onReceive,
}: {
  task: CarrierTask;
  pending: boolean;
  onReceive: (task: CarrierTask, center: { id: string; name: string }) => void;
}) {
  const choiceId = useId();
  const me = useCarrierMe();
  const { dispatchCenterId: viewingCenterId } =
    useOptionalDispatchCenterScope();
  const [chosenCenterId, setChosenCenterId] = useState("");
  const centers = [
    ...new Map(
      (me.data?.carriers ?? [])
        .filter((carrier) => carrier.id === task.carrierId)
        .flatMap((carrier) =>
          carrier.allowedDispatchCenters
            .filter(
              (center) => center.isActive && center.carrierId === task.carrierId
            )
            .map((center) => [center.id, center] as const)
        )
    ).values(),
  ];
  const intendedCenterId = task.dispatchCenterId ?? task.dispatchCenter?.id;
  const center = centers.find(
    (option) => option.id === (intendedCenterId || chosenCenterId)
  );
  const conflict = center && viewingCenterId && center.id !== viewingCenterId;
  const problem = isTaskOnCancellationHold(task)
    ? "On hold · cancellation review"
    : !task.carrierId
      ? "The Task carrier is unavailable. Refresh before receiving."
      : me.isError
        ? "Receiving centers could not be loaded. Refresh before receiving."
        : intendedCenterId && !center && !me.isLoading
          ? "The Task's receiving center is unavailable or outside your access. Refresh before receiving."
          : conflict
            ? `This Task receives at ${center.name}. Change the Dispatch center viewing filter before receiving.`
            : !me.isLoading && centers.length === 0
              ? "No authorized active receiving centers available."
              : null;
  if (task.canReceiveCustomerDropoff === false)
    return (
      <span className="text-xs text-muted-foreground">
        Use intake receiving or resolve custody.
      </span>
    );
  return (
    <div className="relative space-y-2 md:min-w-52">
      {!intendedCenterId && (
        <div className="space-y-1 text-xs">
          <label htmlFor={choiceId}>Choose receiving center</label>
          <NativeSelect
            id={choiceId}
            aria-label={`Choose receiving center for ${taskLabel(task)}`}
            value={chosenCenterId}
            disabled={pending || me.isLoading}
            onChange={(event) => setChosenCenterId(event.target.value)}
          >
            <option value="">Choose receiving center</option>
            {centers.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}
      {center && (
        <p className="text-xs text-muted-foreground">
          Receive at {center.name}
        </p>
      )}
      {problem && (
        <p className="max-w-64 text-xs text-destructive">{problem}</p>
      )}
      <Button
        type="button"
        size="sm-touch"
        className="w-full"
        disabled={pending || !center || Boolean(problem) || me.isLoading}
        aria-label={`Mark ${taskLabel(task)} received`}
        onClick={() => {
          if (center && !problem) onReceive(task, center);
        }}
      >
        {pending ? "Marking received…" : "Mark received"}
      </Button>
    </div>
  );
}
